import { Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { ExternalLink, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ProjectSlackChannelPicker from '@/frontend/features/Fde/ProjectSlackChannelPicker'

export interface ProjectSlackSnapshot {
  status: string
  name: string | null
  error: string | null
  url: string | null
  channel_id: string | null
  connected: boolean
  pending_agents: string[]
  pending_people?: string[]
  copy: Record<string, string>
}

export default function ProjectSlackChannel({ projectId, snapshot, variant = 'card' }: { projectId: string; snapshot: ProjectSlackSnapshot; variant?: 'card' | 'embedded' }) {
  const [busy, setBusy] = useState(false)
  const [channelId, setChannelId] = useState(snapshot.channel_id ?? '')
  const [linkExisting, setLinkExisting] = useState(false)
  const { copy } = snapshot
  const Heading = variant === 'embedded' ? 'h2' : 'h3'
  const recover = snapshot.status === 'delivery_unknown' || linkExisting
  useEffect(() => {
    if (snapshot.status !== 'provisioning') return
    // Poll only this server-owned snapshot while a job is outstanding. A
    // project switch unmounts the keyed card and cancels its pending poll.
    const timer = setInterval(() => router.reload({ only: ['project_slack'] }), 3000)
    return () => clearInterval(timer)
  }, [snapshot.status, projectId])
  const sync = () => {
    setBusy(true)
    router.post('/dashboard/discovery/project_slack_channel', { project_id: projectId, channel_id: recover ? channelId : null },
      { preserveScroll: true, onSuccess: () => setLinkExisting(false), onFinish: () => setBusy(false) })
  }
  return <section aria-labelledby={`project-slack-heading-${projectId}`} className={variant === 'embedded' ? 'border-b border-border pb-6' : 'mx-4 mb-6 rounded-xl border border-border bg-card p-5 md:mx-6'}>
    <div className="flex flex-wrap items-center gap-2">
      <MessageSquare className="size-4 text-muted-foreground" />
      <Heading id={`project-slack-heading-${projectId}`} className="text-sm font-semibold">{copy.title}</Heading>
      <span className="ml-auto text-xs text-muted-foreground">{copy[snapshot.status] ?? copy.unconfigured}</span>
    </div>
    <p className="mt-2 text-sm text-muted-foreground">{copy.description}</p>
    {snapshot.error && <p className="mt-2 text-sm text-muted-foreground" role="status">{snapshot.error}</p>}
    {snapshot.pending_agents.length > 0 && <p className="mt-2 text-sm text-muted-foreground">{copy.pending_agents}: {snapshot.pending_agents.join(', ')}</p>}
    {!!snapshot.pending_people?.length && <p className="mt-2 text-sm text-muted-foreground">{copy.pending_people}: {snapshot.pending_people.join(', ')}</p>}
    <div className="mt-3 flex flex-wrap items-center gap-3">
      {snapshot.url && <a href={snapshot.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline">#{snapshot.name ?? copy.open_channel}<ExternalLink className="size-3.5" /></a>}
      {!snapshot.connected ? <Link href="/dashboard/integrations/slack" className="text-sm font-medium hover:underline">{copy.connect}</Link> : <>
        {snapshot.error && <Link href="/dashboard/integrations/slack" className="text-sm font-medium hover:underline">{copy.review_permissions}</Link>}
        <Button variant="outline" size="sm" disabled={busy || snapshot.status === 'provisioning' || (recover && !channelId.trim())} onClick={sync}>{copy.sync}</Button>
        {snapshot.status !== 'delivery_unknown' && <Button variant="ghost" size="sm" disabled={busy || snapshot.status === 'provisioning'} onClick={() => { setChannelId(snapshot.channel_id ?? ''); setLinkExisting(value => !value) }}>{linkExisting ? (snapshot.url ? copy.keep_channel : copy.cancel_existing) : snapshot.url ? copy.change_channel : copy.link_existing}</Button>}
      </>}
    </div>
    {recover && snapshot.connected && <ProjectSlackChannelPicker projectId={projectId} value={channelId} onChange={setChannelId} current={snapshot.channel_id && snapshot.name ? { id: snapshot.channel_id, name: snapshot.name } : null} copy={copy} />}
    {snapshot.status === 'delivery_unknown' && <p className="mt-2 text-xs text-muted-foreground">{copy.recovery_hint}</p>}
  </section>
}
